import { Formik } from 'formik'
import { debounce, isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import Card from '@/components/Card'
import DeleteConfirmationOverlay from '@/components/DeleteConfirmationOverlay'
import { Text } from '@/components/Form'
import { Search } from '@/components/Icons'
import PaginationTable from '@/components/PaginationTable'

import columns from './columns'
import messages from './messages'

import './index.scss'

function SpaceMembers({
  filterValue,
  isDeleting,
  isFetchingUsers,
  loadPage,
  navigateToEditUser,
  onRemoveFromSpace,
  perPage,
  sortingColumn,
  memberCount,
  members
}: any) {
  const intl = useIntl()

  const defaultSort = {
    displayName: { direction: 'asc', position: 1 }
  }

  // in milliseconds
  const searchDelay = 300

  const [filterValueState, setFilterValueState] = useState(filterValue)
  const [isDeletingState, setIsDeletingState] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [memberToDelete, setMemberToDelete] = useState('')

  useEffect(() => {
    if (!!sortingColumn && !isEmpty(sortingColumn)) {
      fetchFilterResults(
        1,
        Object.keys(sortingColumn)[0],
        sortingColumn[Object.keys(sortingColumn)[0]].direction.toUpperCase()
      )
    } else {
      fetchFilterResults(
        1,
        Object.keys(defaultSort)[0],
        // @ts-expect-error
        defaultSort[Object.keys(defaultSort)[0]].direction.toUpperCase()
      )
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [filterValueState])

  const onRemove = () => {
    setIsDeletingState(false)
    onRemoveFromSpace(memberToDelete)
  }

  const onSelect = (page: any, sort: any, sortBy: any) => {
    setCurrentPage(page)
    loadPage(page, sort, sortBy, filterValueState)
  }

  const showConfirmDelete = (userId: any) => {
    setIsDeletingState(true)
    setMemberToDelete(userId)
  }

  const onCancelDelete = () => {
    setIsDeletingState(false)
    setMemberToDelete('')
  }

  const onFilterChange = (e: any) => {
    setFilterValueState(e.target.value)
    setCurrentPage(1)
  }

  const fetchFilterResults = debounce((page: any, sort?: any, sortBy?: any) => {
    loadPage(page, sort, sortBy, filterValueState)
  }, searchDelay)

  return (
    <Card
      className="space-members"
      isActionInProgress={(isFetchingUsers && !filterValueState) || isDeleting}
      actionMessage={
        isDeleting ? (
          <FormattedMessage {...messages.removeMemberMessage} />
        ) : (
          <FormattedMessage {...messages.loadingMembers} />
        )
      }
    >
      <Card.Content>
        <div className="user-search">
          {/* @ts-expect-error */}
          <Formik initialValues={{ search: filterValueState }}>
            <Text
              icon={isFetchingUsers && filterValueState ? <span className="field__icon--loading" /> : <Search />}
              name="search"
              onChange={onFilterChange}
              placeholder={intl.formatMessage(messages.search)}
            />
          </Formik>
        </div>

        {members.length > 0 || isFetchingUsers ? (
          <PaginationTable
            columns={columns({
              intl,
              onDelete: showConfirmDelete,
              onEdit: navigateToEditUser
            })}
            pagination={{
              count: memberCount,
              startIndex: currentPage * perPage,
              perPage: perPage
            }}
            onPaginateSelect={onSelect}
            rows={members}
            rowItemType={<FormattedMessage {...messages.members} />}
            rowKey="id"
            sortingColumn={sortingColumn}
          />
        ) : (
          <p className="no-members flex-center">
            {filterValueState === '' ? (
              <FormattedMessage {...messages.noUsers} />
            ) : (
              <FormattedMessage {...messages.noSearchUsers} />
            )}
          </p>
        )}
      </Card.Content>

      {!!isDeletingState && (
        <DeleteConfirmationOverlay
          onConfirm={onRemove}
          onCancel={onCancelDelete}
          message={<FormattedMessage {...messages.confirmDelete} />}
          subMessage={<FormattedMessage {...messages.confirmDeleteSubMessage} />}
        />
      )}
    </Card>
  )
}

SpaceMembers.displayName = 'SpaceMembers'
SpaceMembers.propTypes = {
  filterValue: PropTypes.string,
  isDeleting: PropTypes.bool,
  isFetchingUsers: PropTypes.bool,
  loadPage: PropTypes.func.isRequired,
  navigateToEditUser: PropTypes.func.isRequired,
  onRemoveFromSpace: PropTypes.func.isRequired,
  perPage: PropTypes.number.isRequired,
  sortingColumn: PropTypes.object,
  memberCount: PropTypes.number.isRequired,
  members: PropTypes.array
}

export default SpaceMembers
